/**
 * @author Mevan Nirosh
 * @version 0.1
 * @since 9/20/2023
 */
import React, { useState, useEffect } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Stack,
  Alert,
  AlertTitle,
  IconButton,
  Collapse,
} from "@mui/material";
import api from "../../services/AuthApi";
import LoadingButton from "@mui/lab/LoadingButton";
import SaveIcon from "@mui/icons-material/Save";
import CloseIcon from "@mui/icons-material/Close";
import { RequestErrorHandeling } from "../../errorHandeling/RequestErrorHandeling";
import Alerts from "../../errorHandeling/Alerts";
import InfoIcon from "@mui/icons-material/Info";

const EditClientSettingsModal = ({ rowData, open, onClose }) => {
  const [values, setValues] = useState([]);
  const [isBtnLoading, setIsBtnLoading] = useState(false);

  //for success messages
  const [isSuccess, setIsSuccess] = useState(false);
  const [successMessages, setSuccessMessages] = useState([]);

  //for error handling
  const [iserror, setIserror] = useState(false);
  const [iserrorShow, setIserrorShow] = useState(false);
  const [errorMessages, setErrorMessages] = useState([]);

  const [infoAlertOpen, setInfoAlertOpen] = useState(false);

  useEffect(() => {
    setValues(rowData);
  }, [rowData, open]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setValues({
      ...values,
      [name]: value,
    });
    setIserror(false);
    setErrorMessages([]);
  };

  const handleUpdate = (e) => {
    e.preventDefault();
    setIsBtnLoading(true);
    setIserrorShow(true);

    const payload = {
      clientId: rowData.clientId,
      accessTokenTimeToLiveInMin: values.accessTokenTimeToLiveInMin,
      refreshTokenTimeToLiveInMin: values.refreshTokenTimeToLiveInMin,
      authorizationCodeTimeToLiveInMin: values.authorizationCodeTimeToLiveInMin,
    };

    api
      .put("/clients", payload)
      .then((res) => {
        setIsBtnLoading(false);
        setErrorMessages([]);
        setIserror(false);
        setIserrorShow(false);
        setIsSuccess(true);
        setSuccessMessages([res.data?.description]);
        setTimeout(() => {
          setIsSuccess(false);
          setSuccessMessages([""]);
          onClose();
        }, 4000);
      })
      .catch((error) => {
        setIsBtnLoading(false);
        setIsSuccess(false);

        RequestErrorHandeling(
          error,
          setIserror,
          setErrorMessages,
          setIserrorShow,
        );
        setTimeout(() => {
          setIserror(false);
          setErrorMessages([]);
        }, 5000);
      });
  };

  const handleClearData = () => {
    onClose();
    setValues([]);
    setIsBtnLoading(false);
    setIsSuccess(false);
    setSuccessMessages([""]);
    setIserror(false);
    setIserrorShow(false);
    setErrorMessages([]);
    setInfoAlertOpen(false);
  };

  return (
    <div>
      <Dialog
        open={open}
        maxWidth="md"
        fullWidth={true}
        PaperProps={{ style: { borderRadius: "10px" } }}>
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            bgcolor: "#116c6f",
            color: "white",
          }}>
          Update Client Settings
          <IconButton
            aria-label="cancel"
            sx={{ color: "#fff" }}
            onClick={handleClearData}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <form onSubmit={handleUpdate}>
          <DialogContent dividers>
            <div>
              <Alerts
                iserror={iserror}
                errorMessages={errorMessages}
                isSuccess={isSuccess}
                successMessages={successMessages}
                iserrorShow={iserrorShow}
              />
            </div>
            <Stack spacing={2} direction="row" sx={{ marginBottom: 2 }}>
              <TextField
                value={values.clientName ? values.clientName : ""}
                name="clientName"
                type="text"
                variant="outlined"
                color="secondary"
                label="Client Name"
                fullWidth
                disabled
                required
                sx={{ mb: 4 }}
                onChange={handleChange}
              />
            </Stack>
            <label>Live Times in (Minutes)</label>{" "}
            <IconButton onClick={() => setInfoAlertOpen(true)}>
              <InfoIcon fontSize="small" />
            </IconButton>
            <br />
            <Collapse in={infoAlertOpen}>
              <Alert severity="info" onClose={() => setInfoAlertOpen(false)}>
                <AlertTitle>
                  <strong>Authorization Code</strong>
                </AlertTitle>
                It is a temporary code that the client will exchange for an
                access token. Minimum & Maximum time to expire can be set to 1 &
                5 mins respectively.
                <br />
                <br />
                <AlertTitle>
                  <strong>Access Token</strong>
                </AlertTitle>
                It is returned after the authorization code has been exchanged
                and used to access an API. The mininmum time to expire can be
                set to 4 min.
                <br />
                <br />
                <AlertTitle>
                  <strong>Refresh Token</strong>
                </AlertTitle>
                When access tokens expire or become invalid a refresh token
                allows the application to obtain a new access token without
                prompting the user. The mininmum time to expire can be set to 10
                min.
              </Alert>
            </Collapse>
            <br />
            <br />
            <Stack spacing={2} direction="row" sx={{ marginBottom: 2 }}>
              <TextField
                value={
                  values.accessTokenTimeToLiveInMin
                    ? values.accessTokenTimeToLiveInMin
                    : ""
                }
                name="accessTokenTimeToLiveInMin"
                type="number"
                variant="outlined"
                color="secondary"
                label="Access Token"
                fullWidth
                required
                sx={{ mb: 4 }}
                onChange={handleChange}
              />
              <TextField
                value={
                  values.refreshTokenTimeToLiveInMin
                    ? values.refreshTokenTimeToLiveInMin
                    : ""
                }
                name="refreshTokenTimeToLiveInMin"
                type="number"
                variant="outlined"
                color="secondary"
                label="Refresh Token"
                fullWidth
                required
                sx={{ mb: 4 }}
                onChange={handleChange}
              />
              <TextField
                value={
                  values.authorizationCodeTimeToLiveInMin
                    ? values.authorizationCodeTimeToLiveInMin
                    : ""
                }
                name="authorizationCodeTimeToLiveInMin"
                type="number"
                variant="outlined"
                color="secondary"
                label="Auth Code"
                fullWidth
                required
                sx={{ mb: 4 }}
                onChange={handleChange}
              />
            </Stack>
          </DialogContent>
          <DialogActions sx={{ p: "1.25rem" }}>
            <Button onClick={handleClearData} variant="outlined" color="error">
              Cancel
            </Button>
            <LoadingButton
              loading={isBtnLoading}
              loadingPosition="start"
              startIcon={<SaveIcon />}
              variant="contained"
              color="success"
              type="submit">
              Update
            </LoadingButton>
          </DialogActions>
        </form>
      </Dialog>
    </div>
  );
};

export default EditClientSettingsModal;
